import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleIcon from "@mui/icons-material/CheckCircleOutlined";
import CropLandscapeIcon from "@mui/icons-material/CropLandscape";
import CropPortraitIcon from "@mui/icons-material/CropPortrait";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import PermMediaIcon from "@mui/icons-material/PermMediaOutlined";
import TvOutlinedIcon from "@mui/icons-material/TvOutlined";
import WorkspacesOutlinedIcon from "@mui/icons-material/WorkspacesOutlined";
import { Trans, useTranslation } from "react-i18next";
import { tokens } from "../theme";
import { useFormat } from "../i18n/format";
import type { Translate } from "../i18n/translate";
import AssetsPicker from "./AssetsPicker";
import { ItemFormSurface, type Dismiss } from "./EditDrawer";
import type { AssetSummary } from "../http/foyer";
import type { DayOfWeek } from "@shared/types/foyer";
import type { SignageCampaign, SignageItem } from "@shared/types/signage";
import {
  combinedOrientation,
  durationLabel,
  type MediaOrientation,
  type ScreenOrientation,
} from "@shared/media";
import {
  byUrl,
  itemFromAsset,
  itemOrientation,
  thumbnailFor,
} from "../utils/signageItems";
import { PermissionGate } from "../auth/access";

/**
 * Create or edit a campaign.
 *
 * A campaign is media, a recurrence rule, and the units it runs on. The rule
 * is stored exactly as it is drawn here - local dates, local `HH:mm`, a weekday
 * set - and turned into instants at resolve time against the location's
 * timezone. Storing resolved instants instead would drift the whole booking by
 * an hour at every DST boundary.
 *
 * The target list is NOT part of the campaign record. A campaign is owned by a
 * layer; which UNITS and unit GROUPS run it (UNIT-4) is stored on their
 * schedules, in `SignageAssignment.campaignIds`. That is what lets one
 * chain-level campaign be booked by four of a customer's twelve locations
 * without four copies of it. A screen swapped in a unit keeps the unit's
 * campaigns.
 */

/**
 * Something a campaign can be booked onto: a unit, or a unit group.
 * `id` is `unit:<rooms.id>` or `group:<group id>`, so the two never collide.
 */
export interface CampaignTarget {
  id: string;
  kind: "unit" | "group";
  /** What somebody calls it: "Unit 101", or the group's name. */
  label: string;
  /** What hangs there, or how many units a group holds. */
  detail: string;
  /** Which location. Blank where the scope has only one. */
  premiseLabel: string;
  /** How it is hung. A group of both shapes is `mixed`. */
  orientation: ScreenOrientation | "mixed";
  /** A unit with no setting and no report: it plays as landscape. */
  assumed?: boolean;
}

/** Does media of this shape play right on that target? */
export function campaignFits(
  media: MediaOrientation | "mixed",
  target: ScreenOrientation | "mixed"
): boolean {
  return media === "both" || media === target;
}

const DAYS: DayOfWeek[] = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
/** A Monday, so `DAYS[i]` is `WEEK_START + i` days. */
const WEEK_START = Date.UTC(2024, 0, 1);
const LIST_SEPARATOR = ", ";

/** What a new image shows for. Each item then has its own, set in its row. */
const DEFAULT_ITEM_SECONDS = 10;
/**
 * Past this a campaign is a long interruption of the loop, so the dialog says
 * so. A warning, not a limit: a customer may well want it.
 */
export const LONG_CAMPAIGN_SECONDS = 60;
/** The model clamps an item to an hour; the field says so before it does. */
const MAX_ITEM_SECONDS = 3600;

/**
 * How long one item holds the screen.
 *
 * A clip with a measured length plays for exactly that - it is not ours to cut
 * or stretch - so its row shows the length and offers no field. Everything
 * else (an image, or a clip nobody has measured) is the operator's to set.
 */
const clipSeconds = (
  it: SignageItem,
  asset: AssetSummary | undefined
): number | null => {
  if (it.kind !== "video" || !asset?.durationSeconds) return null;
  return Math.ceil(asset.durationSeconds);
};
export const NAME_MAX = 100;

/** `00:00`-`00:00` is how the model spells all day. */
const ALL_DAY = { start: "00:00", end: "00:00" };

export interface SignageCampaignDialogProps {
  open: boolean;
  onClose: () => void;
  /** Absent = creating a new one. */
  campaign?: SignageCampaign | null;
  /** Units and unit groups in scope - what the campaign can be booked onto. */
  targets: CampaignTarget[];
  /** Targets already running this campaign, by `CampaignTarget.id`. */
  bookedTargetIds: string[];
  /** The library, for media thumbnails. */
  assets: AssetSummary[];
  /** Persist. Resolves true on success, which closes the surface. */
  onSave: (
    patch: Partial<SignageCampaign>,
    targetIds: string[]
  ) => Promise<boolean>;
  saving?: boolean;
  error?: string | null;
}

const FORMATS: ScreenOrientation[] = ["landscape", "portrait"];

export default function SignageCampaignDialog({
  open,
  onClose,
  campaign,
  targets,
  bookedTargetIds,
  assets,
  onSave,
  saving,
  error,
}: SignageCampaignDialogProps) {
  const { t } = useTranslation(["signage", "common", "glossary"]);
  const fmt = useFormat();
  const dayLabel = (i: number) =>
    fmt.date(WEEK_START + i * 86_400_000, {
      weekday: "short",
      timeZone: "UTC",
    });
  const orientationWord = (o: ScreenOrientation) =>
    t(`signageScreenSettingsDialog.orientationInline.${o}`);
  const orientationLabel = (o: ScreenOrientation) =>
    o === "portrait"
      ? t("common:labels.portrait")
      : t("common:labels.landscape");
  const [name, setName] = useState("");
  const [items, setItems] = useState<SignageItem[]>([]);
  const [priority, setPriority] = useState<"normal" | "high">("normal");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [openEnded, setOpenEnded] = useState(true);
  const [startTime, setStartTime] = useState("08:00");
  const [endTime, setEndTime] = useState("20:00");
  const [allDay, setAllDay] = useState(false);
  const [days, setDays] = useState<DayOfWeek[]>([]);
  const [format, setFormat] = useState<ScreenOrientation>("landscape");
  const [picked, setPicked] = useState<string[]>([]);
  const [pickerOpen, setPickerOpen] = useState(false);

  const bookedKey = bookedTargetIds.join(",");

  useEffect(() => {
    if (!open) return;
    setName(campaign?.name ?? "");
    setItems(campaign?.items ?? []);
    setPriority(campaign?.priority ?? "normal");
    setStartDate(campaign?.startDate ?? "");
    setEndDate(campaign?.endDate ?? "");
    setOpenEnded(!campaign?.endDate);
    setDays(campaign?.days ?? []);
    setPicked(bookedTargetIds);

    const s = campaign?.startTime ?? ALL_DAY.start;
    const e = campaign?.endTime ?? ALL_DAY.end;
    const whole = s === e;
    setAllDay(campaign ? whole : false);
    setStartTime(whole ? "08:00" : s);
    setEndTime(whole ? "20:00" : e);

    // Open on the format the booked units already are, so editing does not
    // silently re-scope a portrait campaign to the landscape tab.
    const booked = targets.filter(
      (x) => bookedTargetIds.includes(x.id) && x.orientation !== "mixed"
    );
    const shaped = booked[0]?.orientation;
    setFormat(shaped === "portrait" ? "portrait" : "landscape");
    // Keyed on the CONTENT of the booked list, not its identity: the caller
    // rebuilds that array every render, and depending on it directly would
    // re-seed on each one - wiping a unit the operator had just ticked.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, campaign, bookedKey, targets]);

  /**
   * Assets picked in THIS dialog.
   *
   * The `assets` prop is the library as the page last loaded it, and a file
   * uploaded or picked since is not in it - so the new item had no asset to
   * draw a thumbnail from (the preview showed the duration over nothing) and
   * no measured length for a clip, until the dialog was closed and reopened
   * on a refreshed list. What was just picked is known here, so it is kept.
   */
  const [pickedAssets, setPickedAssets] = useState<AssetSummary[]>([]);
  const library = useMemo(
    () => byUrl([...assets, ...pickedAssets]),
    [assets, pickedAssets]
  );

  const itemsOrientation = combinedOrientation(
    items.map((it) => itemOrientation(it, library, []))
  );

  /** How many units each format has, for the two cards. */
  const countByFormat = useMemo(() => {
    const out: Record<ScreenOrientation, number> = {
      landscape: 0,
      portrait: 0,
    };
    for (const t of targets) {
      if (t.kind === "unit" && t.orientation !== "mixed") {
        out[t.orientation] += 1;
      }
    }
    return out;
  }, [targets]);

  /*
   * Units and groups of the chosen shape. A unit always HAS a shape now
   * (UNIT-2): its setting, else what its screen reports, else landscape, and
   * the server holds a campaign to exactly that - so there is no "shape not
   * known" bucket to offer under both formats any more. A unit whose shape is
   * only assumed is labelled so.
   *
   * A group of both shapes is listed under both formats: it takes a campaign
   * whose media fits both, and the row says so.
   */
  const listed = useMemo(
    () =>
      targets.filter(
        (t) => t.orientation === format || t.orientation === "mixed"
      ),
    [targets, format]
  );

  /** The picked targets this campaign's media would play wrong on. */
  const unfit = useMemo(
    () =>
      targets.filter(
        (t) =>
          picked.includes(t.id) &&
          !campaignFits(itemsOrientation, t.orientation)
      ),
    [targets, picked, itemsOrientation]
  );

  const listedIds = listed.map((s) => s.id);
  const selectedHere = picked.filter((id) => listedIds.includes(id));
  const allSelected =
    listed.length > 0 && selectedHere.length === listed.length;

  const toggleScreen = (id: string) =>
    setPicked((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  const toggleAll = () =>
    setPicked((prev) =>
      allSelected
        ? prev.filter((id) => !listedIds.includes(id))
        : [...new Set([...prev, ...listedIds])]
    );

  const addAsset = (asset: AssetSummary) => {
    setPickedAssets((prev) => [...prev, asset]);
    setItems((prev) => [
      ...prev,
      itemFromAsset(asset, prev.length, DEFAULT_ITEM_SECONDS),
    ]);
    setPickerOpen(false);
  };

  /**
   * The items as they will be saved: a measured clip plays for its own length,
   * so that is what is written whatever an older save left behind.
   */
  const timedItems = useMemo(
    () =>
      items.map((i) => {
        const clip = clipSeconds(i, library.get(i.assetUrl));
        return clip === null ? i : { ...i, durationSeconds: clip };
      }),
    [items, library]
  );
  /** The campaign is as long as its items are, and nothing else sets it. */
  const totalSeconds = timedItems.reduce(
    (sum, i) => sum + Math.max(0, i.durationSeconds),
    0
  );
  const tooLong = totalSeconds > LONG_CAMPAIGN_SECONDS;
  const untimed = timedItems.some((i) => !(i.durationSeconds > 0));

  const setItemSeconds = (index: number, raw: string) =>
    setItems((prev) =>
      prev.map((it, xi) => {
        if (xi !== index) return it;
        const n = Math.round(Number(raw));
        return {
          ...it,
          durationSeconds: Number.isFinite(n)
            ? Math.min(MAX_ITEM_SECONDS, Math.max(0, n))
            : 0,
        };
      })
    );

  /** Every reason this campaign is not ready, in the order they appear above. */
  const missing = [
    name.trim() ? null : t("signageCampaignDialog.missing.name"),
    items.length > 0 ? null : t("signageCampaignDialog.missing.media"),
    untimed ? t("signageCampaignDialog.missing.displayTime") : null,
    picked.length > 0 ? null : t("signageCampaignDialog.missing.target"),
    unfit.length > 0 ? t("signageCampaignDialog.missing.fittingMedia") : null,
  ].filter(Boolean) as string[];
  const ready = missing.length === 0;

  /** The page saves; on success the surface slides away, then closes. */
  const submit = (dismiss: Dismiss) =>
    void onSave(
      {
        name: name.trim(),
        // Each item keeps the time it was given; the campaign's length is
        // their sum and is stored nowhere else.
        items: timedItems,
        enabled: true,
        priority,
        startDate: startDate || null,
        endDate: openEnded ? null : endDate || null,
        days,
        startTime: allDay ? ALL_DAY.start : startTime,
        endTime: allDay ? ALL_DAY.end : endTime,
      },
      picked
    ).then((ok) => ok && dismiss());

  return (
    <ItemFormSurface
      mode={campaign ? "edit" : "create"}
      open={open}
      onClose={onClose}
      title={
        campaign
          ? t("signageCampaignDialog.editTitle")
          : t("signageCampaignDialog.createTitle")
      }
      subtitle={t("signageCampaignDialog.subtitle")}
      icon={<AddIcon />}
      maxWidth="xl"
      drawerWidth={1240}
      actions={(dismiss) => (
        <>
          <Button onClick={() => dismiss()} color="inherit" variant="outlined">
            {t("common:buttons.cancel")}
          </Button>
          <PermissionGate
            resource="signage"
            level="edit"
            deniedProps={{ disabled: true }}
          >
            <Button
              variant="contained"
              disabled={!ready || saving}
              onClick={() => submit(dismiss)}
              endIcon={<AddIcon />}
              data-testid="campaign-submit"
            >
              {saving
                ? t("common:states.saving")
                : campaign
                  ? t("signageCampaignDialog.saveCampaign")
                  : t("signageCampaignDialog.createTitle")}
            </Button>
          </PermissionGate>
        </>
      )}
    >
      <Stack direction={{ xs: "column", lg: "row" }} gap={5}>
        {/* ---------------------------------------------------- the form */}
        <Stack gap={5} sx={{ flex: 1, minWidth: 0 }}>
          <Section
            title={t("signageCampaignDialog.nameSection")}
            hint={t("signageCampaignDialog.nameHint")}
          >
            <TextField
              size="small"
              fullWidth
              placeholder={t("signageCampaignDialog.namePlaceholder")}
              value={name}
              onChange={(e) => setName(e.target.value.slice(0, NAME_MAX))}
              inputProps={{ "data-testid": "campaign-name" }}
              InputProps={{
                endAdornment: (
                  <Typography
                    variant="meta"
                    sx={{
                      fontWeight: "fontWeightSemibold",
                      color: tokens.inkMuted,
                      whiteSpace: "nowrap",
                      pl: 1,
                    }}
                  >
                    {name.length} / {NAME_MAX}
                  </Typography>
                ),
              }}
            />
          </Section>

          <Section
            title={t("signageCampaignDialog.formatSection")}
            hint={t("signageCampaignDialog.formatHint")}
          >
            <Stack direction={{ xs: "column", sm: "row" }} gap={2}>
              {FORMATS.map((f) => (
                <FormatCard
                  key={f}
                  format={f}
                  count={countByFormat[f]}
                  selected={format === f}
                  onClick={() => setFormat(f)}
                />
              ))}
            </Stack>
          </Section>

          <Box>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: 2 }}
            >
              <Typography variant="body2" sx={{ color: tokens.ink }}>
                {t("signageCampaignDialog.chooseUnits", {
                  orientation: orientationWord(format),
                })}
              </Typography>
              <Stack direction="row" alignItems="center" gap={3}>
                <Typography variant="body2" sx={{ color: tokens.inkMuted }}>
                  {t("signageCampaignDialog.selectedOf", {
                    selected: selectedHere.length,
                    total: listed.length,
                  })}
                </Typography>
                <Button
                  size="small"
                  onClick={toggleAll}
                  disabled={listed.length === 0}
                >
                  {allSelected
                    ? t("common:buttons.clearAll")
                    : t("common:buttons.selectAll")}
                </Button>
              </Stack>
            </Stack>

            {listed.length === 0 ? (
              <Box
                sx={{
                  border: `1px dashed ${tokens.divider}`,
                  borderRadius: "8px",
                  p: 3,
                  textAlign: "center",
                }}
              >
                {/*
                    Two reasons this list is empty, and they need different
                    things done: no signage unit at all, or none of this shape.
                  */}
                <Typography variant="bodySm" sx={{ color: tokens.inkMuted }}>
                  {targets.length === 0
                    ? t("signageCampaignDialog.noUnitsAtAll")
                    : t("signageCampaignDialog.noUnitsOfShape", {
                        orientation: orientationWord(format),
                      })}
                </Typography>
              </Box>
            ) : (
              <Box
                sx={{
                  border: `1px solid ${tokens.divider}`,
                  borderRadius: "8px",
                  overflow: "hidden",
                }}
              >
                {listed.map((s, i) => {
                  const on = picked.includes(s.id);
                  const wrong =
                    on && !campaignFits(itemsOrientation, s.orientation);
                  const Icon =
                    s.kind === "group"
                      ? WorkspacesOutlinedIcon
                      : TvOutlinedIcon;
                  return (
                    <Stack
                      key={s.id}
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      onClick={() => toggleScreen(s.id)}
                      data-testid={`campaign-target-${s.id}`}
                      sx={{
                        height: 56,
                        px: 1.5,
                        cursor: "pointer",
                        borderTop:
                          i === 0 ? "none" : `1px solid ${tokens.divider}`,
                        bgcolor: on ? tokens.brandTintSoft : "transparent",
                        "&:hover": { bgcolor: tokens.brandTintSoft },
                      }}
                    >
                      <Stack direction="row" alignItems="center" gap={1.75}>
                        <Icon sx={{ fontSize: 20, color: tokens.inkMuted }} />
                        <Box>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: "fontWeightSemibold" }}
                          >
                            {s.label}
                          </Typography>
                          <Typography
                            variant="meta"
                            sx={{ color: tokens.inkMuted }}
                          >
                            {s.detail}
                            {s.premiseLabel ? ` · ${s.premiseLabel}` : ""}
                            {/* Said out loud: the one shape here that is not
                                  a fact, and the one that limits the media. */}
                            {s.assumed &&
                              ` ${t("signageCampaignDialog.assumedShape")}`}
                            {s.orientation === "mixed" &&
                              ` ${t("signageCampaignDialog.mixedGroup")}`}
                          </Typography>
                          {wrong && (
                            <Typography
                              variant="meta"
                              data-testid={`campaign-target-unfit-${s.id}`}
                              sx={{ color: tokens.error }}
                            >
                              {t("signageCampaignDialog.mediaWrongWay", {
                                orientation:
                                  itemsOrientation === "mixed"
                                    ? t(
                                        "signageCampaignDialog.mixedOrientation"
                                      )
                                    : orientationWord(
                                        itemsOrientation as ScreenOrientation
                                      ),
                              })}
                            </Typography>
                          )}
                        </Box>
                      </Stack>
                      <Stack direction="row" alignItems="center" gap={2}>
                        <Checkbox
                          size="small"
                          checked={on}
                          inputProps={{
                            "aria-label": t("signageCampaignDialog.include", {
                              name: s.label,
                            }),
                          }}
                          onClick={(e) => e.stopPropagation()}
                          onChange={() => toggleScreen(s.id)}
                        />
                      </Stack>
                    </Stack>
                  );
                })}
              </Box>
            )}
          </Box>

          <Section
            title={t("signageCampaignDialog.scheduleSection")}
            hint={t("signageCampaignDialog.scheduleHint")}
          >
            <Stack gap={3}>
              <Stack direction={{ xs: "column", md: "row" }} gap={3}>
                <Stack gap={1} sx={{ flex: 1 }}>
                  <Label>{t("signageCampaignDialog.dateRange")}</Label>
                  <Stack direction="row" alignItems="center" gap={2}>
                    <TextField
                      size="small"
                      type="date"
                      label={t("common:labels.from")}
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      sx={{ flex: 1 }}
                    />
                    <TextField
                      size="small"
                      type="date"
                      label={t("signageCampaignDialog.until")}
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      disabled={openEnded}
                      sx={{ flex: 1 }}
                    />
                    <Stack direction="row" alignItems="center">
                      <Checkbox
                        size="small"
                        checked={openEnded}
                        onChange={(e) => setOpenEnded(e.target.checked)}
                        inputProps={{
                          "aria-label": t("common:labels.noEndDate"),
                        }}
                      />
                      <Typography
                        variant="meta"
                        sx={{ color: tokens.inkMuted }}
                      >
                        {t("common:labels.noEndDate")}
                      </Typography>
                    </Stack>
                  </Stack>
                </Stack>

                <Stack gap={1}>
                  <Label>{t("common:labels.days")}</Label>
                  <Stack direction="row" gap={1} flexWrap="wrap">
                    {DAYS.map((d, di) => {
                      const on = days.includes(d);
                      return (
                        <Box
                          key={d}
                          role="button"
                          aria-pressed={on}
                          aria-label={dayLabel(di)}
                          tabIndex={0}
                          onClick={() =>
                            setDays((prev) =>
                              prev.includes(d)
                                ? prev.filter((x) => x !== d)
                                : [...prev, d]
                            )
                          }
                          sx={{
                            px: 2,
                            py: 0.5,
                            borderRadius: "6px",
                            cursor: "pointer",
                            typography: "meta",
                            fontWeight: "fontWeightSemibold",
                            border: `1px solid ${
                              on ? tokens.brand : tokens.divider
                            }`,
                            bgcolor: on ? tokens.brandTintSoft : "transparent",
                            color: on ? tokens.brand : tokens.inkMuted,
                          }}
                        >
                          {dayLabel(di)}
                        </Box>
                      );
                    })}
                  </Stack>
                  <Typography variant="meta" sx={{ color: tokens.inkMuted }}>
                    {days.length === 0
                      ? t("signageCampaignDialog.daysNone")
                      : t("signageCampaignDialog.runsOnDays", {
                          count: days.length,
                        })}
                  </Typography>
                </Stack>
              </Stack>

              <Stack gap={1} sx={{ maxWidth: 560 }}>
                <Label>{t("signageCampaignDialog.timeOfDay")}</Label>
                <Stack direction="row" alignItems="center" gap={2}>
                  <TextField
                    size="small"
                    type="time"
                    label={t("common:labels.from")}
                    value={startTime}
                    onChange={(e) => setStartTime(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    disabled={allDay}
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    size="small"
                    type="time"
                    label={t("signageCampaignDialog.until")}
                    value={endTime}
                    onChange={(e) => setEndTime(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    disabled={allDay}
                    sx={{ flex: 1 }}
                  />
                  <Stack direction="row" alignItems="center">
                    <Checkbox
                      size="small"
                      checked={allDay}
                      onChange={(e) => setAllDay(e.target.checked)}
                      inputProps={{
                        "aria-label": t("signageCampaignDialog.allDay"),
                      }}
                    />
                    <Typography variant="meta" sx={{ color: tokens.inkMuted }}>
                      {t("signageCampaignDialog.allDay")}
                    </Typography>
                  </Stack>
                </Stack>
              </Stack>
            </Stack>
          </Section>

          <Section
            title={t("signageCampaignDialog.mediaSection")}
            hint={t("signageCampaignDialog.mediaHint")}
          >
            {items.length === 0 ? (
              <Box
                sx={{
                  border: `1px solid ${tokens.divider}`,
                  borderRadius: "8px",
                  py: 5,
                  display: "grid",
                  placeItems: "center",
                }}
              >
                <Stack direction="row" alignItems="center" gap={2}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: "6px",
                      bgcolor: tokens.brandTintSoft,
                      color: tokens.brand,
                      display: "grid",
                      placeItems: "center",
                    }}
                  >
                    <PermMediaIcon />
                  </Box>
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: "fontWeightBold" }}
                    >
                      {t("signageCampaignDialog.noMediaChosen")}
                    </Typography>
                    <Button
                      size="small"
                      endIcon={<ArrowForwardIcon />}
                      onClick={() => setPickerOpen(true)}
                      data-testid="campaign-choose-media"
                      sx={{ px: 0 }}
                    >
                      {t("signageCampaignDialog.chooseMedia")}
                    </Button>
                  </Box>
                </Stack>
              </Box>
            ) : (
              <Stack gap={1}>
                {items.map((it, i) => {
                  const asset = library.get(it.assetUrl);
                  const thumb = thumbnailFor(asset);
                  const clip = clipSeconds(it, asset);
                  return (
                    <Stack
                      key={it.id}
                      direction="row"
                      alignItems="center"
                      gap={1.5}
                      sx={{
                        p: 1,
                        border: `1px solid ${tokens.divider}`,
                        borderRadius: "8px",
                      }}
                    >
                      <Box
                        sx={{
                          width: 56,
                          height: 40,
                          flexShrink: 0,
                          borderRadius: "6px",
                          bgcolor: tokens.rowAlt,
                          overflow: "hidden",
                          display: "grid",
                          placeItems: "center",
                        }}
                      >
                        {/* A clip shows its captured poster - its own URL in
                              an <img> renders nothing. */}
                        {thumb ? (
                          <Box
                            component="img"
                            src={thumb}
                            alt=""
                            sx={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                          />
                        ) : (
                          <PermMediaIcon
                            sx={{ fontSize: 18, color: tokens.inkFaint }}
                          />
                        )}
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="bodySm" noWrap>
                          {asset?.originalName ?? it.assetUrl.split("/").pop()}
                        </Typography>
                        <Typography
                          variant="meta"
                          sx={{ color: tokens.inkMuted }}
                        >
                          {it.kind === "video"
                            ? t("common:labels.clip")
                            : t("common:labels.image")}
                          {asset?.orientation
                            ? ` · ${
                                asset.orientation === "both"
                                  ? t("common:labels.fitsBoth")
                                  : orientationLabel(asset.orientation)
                              }`
                            : ""}
                        </Typography>
                      </Box>
                      {clip !== null ? (
                        <Typography
                          variant="meta"
                          data-testid="campaign-item-clip-seconds"
                          sx={{
                            color: tokens.inkMuted,
                            width: 96,
                            textAlign: "right",
                          }}
                        >
                          {t("signageCampaignDialog.clipLength", {
                            seconds: clip,
                          })}
                        </Typography>
                      ) : (
                        <TextField
                          size="small"
                          type="number"
                          value={
                            it.durationSeconds > 0 ? it.durationSeconds : ""
                          }
                          onChange={(e) => setItemSeconds(i, e.target.value)}
                          error={!(it.durationSeconds > 0)}
                          sx={{ width: 96 }}
                          inputProps={{
                            min: 1,
                            max: MAX_ITEM_SECONDS,
                            step: 1,
                            "aria-label": t(
                              "signageCampaignDialog.displayTimeFor",
                              {
                                name:
                                  asset?.originalName ??
                                  t("signageCampaignDialog.itemFallback"),
                              }
                            ),
                            "data-testid": "campaign-item-seconds",
                          }}
                          InputProps={{
                            endAdornment: (
                              <Typography
                                variant="meta"
                                sx={{ color: tokens.inkMuted }}
                              >
                                s
                              </Typography>
                            ),
                          }}
                        />
                      )}
                      <IconButton
                        size="small"
                        aria-label={t("signageCampaignDialog.removeMedia")}
                        onClick={() =>
                          setItems((prev) => prev.filter((_, xi) => xi !== i))
                        }
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  );
                })}
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => setPickerOpen(true)}
                  sx={{ alignSelf: "flex-start" }}
                >
                  {t("signageCampaignDialog.addMedia")}
                </Button>
                <Typography
                  variant="meta"
                  data-testid="campaign-total-seconds"
                  sx={{ color: tokens.inkMuted }}
                >
                  <Trans
                    t={t}
                    i18nKey="signageCampaignDialog.totalDisplayTime"
                    values={{ seconds: totalSeconds }}
                    components={{ 1: <strong /> }}
                  />
                </Typography>
                {tooLong && (
                  <Stack
                    direction="row"
                    gap={1}
                    data-testid="campaign-too-long"
                    sx={{
                      p: 1.25,
                      borderRadius: "8px",
                      border: `1px solid ${tokens.warning}`,
                      bgcolor: tokens.warningTint,
                    }}
                  >
                    <ErrorOutlineIcon
                      sx={{ fontSize: 18, color: tokens.warning }}
                    />
                    <Typography variant="meta" sx={{ color: tokens.ink }}>
                      {t("signageCampaignDialog.tooLong", {
                        seconds: totalSeconds,
                        limit: LONG_CAMPAIGN_SECONDS,
                      })}
                    </Typography>
                  </Stack>
                )}
              </Stack>
            )}
          </Section>

          <Section title={t("signageCampaignDialog.displaySettings")}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              gap={3}
              alignItems={{ sm: "center" }}
            >
              <Box sx={{ width: 234 }}>
                <Label>{t("common:labels.priority")}</Label>
                <Typography variant="meta" sx={{ color: tokens.inkMuted }}>
                  {t("signageCampaignDialog.priorityHint")}
                </Typography>
              </Box>
              <Stack direction="row" gap={1}>
                {(["normal", "high"] as const).map((p) => (
                  <Box
                    key={p}
                    role="button"
                    aria-pressed={priority === p}
                    tabIndex={0}
                    onClick={() => setPriority(p)}
                    sx={{
                      px: 2,
                      py: 1,
                      borderRadius: "8px",
                      cursor: "pointer",
                      typography: "meta",
                      fontWeight: "fontWeightMedium",
                      border: `1px solid ${
                        priority === p ? tokens.brand : tokens.divider
                      }`,
                      bgcolor:
                        priority === p ? tokens.brandTintSoft : "transparent",
                      color: priority === p ? tokens.brand : tokens.ink,
                    }}
                  >
                    {p === "high"
                      ? t("signageCampaignDialog.highPriority")
                      : t("signageCampaignDialog.normal")}
                  </Box>
                ))}
              </Stack>
            </Stack>
          </Section>
        </Stack>

        {/* ------------------------------------------------- the preview */}
        <Box
          sx={{
            width: { xs: "100%", lg: 356 },
            flexShrink: 0,
            borderLeft: { lg: `1px solid ${tokens.divider}` },
            pl: { lg: 4 },
          }}
        >
          <Typography
            sx={{
              typography: "title",
              fontWeight: "fontWeightSemibold",
              color: tokens.ink,
            }}
          >
            {t("signageCampaignDialog.preview")}
          </Typography>
          <Typography variant="meta" sx={{ color: tokens.inkMuted, mb: 2 }}>
            {t("signageCampaignDialog.previewHint")}
          </Typography>

          <PreviewMedia
            items={items}
            library={library}
            seconds={totalSeconds}
          />

          <Box
            sx={{
              border: `1px solid ${tokens.divider}`,
              borderRadius: "8px",
              px: 1.5,
              mt: 3,
            }}
          >
            <SummaryRow
              label={t("common:labels.name")}
              value={name || t("common:labels.notSet")}
            />
            <SummaryRow
              label={t("signageCampaignDialog.formatSection")}
              value={orientationLabel(format)}
            />
            <SummaryRow
              label={t("common:labels.media")}
              value={
                items.length === 0
                  ? t("common:labels.notChosen")
                  : t("signageChangeChannelDialog.itemCount", {
                      count: items.length,
                    })
              }
            />
            <SummaryRow
              label={t("signageCampaignDialog.displayTime")}
              value={t("signageCampaignDialog.secondsCount", {
                count: totalSeconds,
              })}
              accent={tooLong}
            />
            <SummaryRow
              label={t("signageCampaignDialog.dates")}
              value={
                startDate
                  ? t("signageCampaignDialog.range", {
                      start: startDate,
                      end:
                        openEnded || !endDate
                          ? t("signageCampaignDialog.noEnd")
                          : endDate,
                    })
                  : t("signageCampaignDialog.immediately")
              }
            />
            <SummaryRow
              label={t("common:labels.days")}
              value={
                days.length === 0
                  ? t("signageCampaignDialog.everyDay")
                  : DAYS.map((d, di) => (days.includes(d) ? dayLabel(di) : ""))
                      .filter(Boolean)
                      .join(LIST_SEPARATOR)
              }
            />
            <SummaryRow
              label={t("common:labels.time")}
              value={
                allDay
                  ? t("signageCampaignDialog.allDay")
                  : t("signageCampaignDialog.range", {
                      start: startTime,
                      end: endTime,
                    })
              }
            />
            <SummaryRow
              label={t("common:labels.priority")}
              value={
                priority === "high"
                  ? t("signageCampaignDialog.highPriority")
                  : t("signageCampaignDialog.normal")
              }
              accent={priority === "high"}
            />
            <SummaryRow
              label={t("signageCampaignDialog.runsOn")}
              value={targetSummary(t, targets, picked)}
              last
            />
          </Box>

          {/*
              Says what is still missing, not just that something is.
              "Fill in all the information" leaves an operator hunting up a
              form this tall for the one field they skipped.
            */}
          <Stack
            direction="row"
            gap={1.5}
            sx={{
              mt: 3,
              p: 1.75,
              borderRadius: "8px",
              border: `1px solid ${ready ? tokens.success : tokens.warning}`,
              bgcolor: ready ? tokens.successTint : tokens.warningTint,
            }}
          >
            {ready ? (
              <CheckCircleIcon sx={{ fontSize: 20, color: tokens.success }} />
            ) : (
              <ErrorOutlineIcon sx={{ fontSize: 20, color: tokens.warning }} />
            )}
            <Box>
              <Typography
                variant="meta"
                sx={{
                  fontWeight: "fontWeightSemibold",
                  color: ready ? tokens.success : tokens.warning,
                }}
              >
                {ready
                  ? t("signageCampaignDialog.ready")
                  : t("signageCampaignDialog.notReady")}
              </Typography>
              <Typography variant="meta" sx={{ color: tokens.inkMuted }}>
                {ready
                  ? t("signageCampaignDialog.readyHint")
                  : t("signageCampaignDialog.stillNeeds", {
                      list: missing.join(LIST_SEPARATOR),
                    })}
              </Typography>
            </Box>
          </Stack>

          {error && (
            <Typography variant="bodySm" sx={{ color: tokens.error, mt: 2 }}>
              {error}
            </Typography>
          )}
        </Box>
      </Stack>

      <AssetsPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={(_url, asset) => addAsset(asset)}
        // The campaign's own items decide once it has any; before that, the
        // format its screens are (UNIT-1). The server refuses a mixed list.
        fitsOrientation={
          itemsOrientation === "landscape" || itemsOrientation === "portrait"
            ? itemsOrientation
            : format
        }
      />
    </ItemFormSurface>
  );
}

/* -------------------------------------------------------------------------- */

/** "2 units, 1 group" - what the summary says the campaign runs on. */
function targetSummary(
  t: Translate,
  targets: CampaignTarget[],
  picked: string[]
): string {
  const chosen = targets.filter((x) => picked.includes(x.id));
  const units = chosen.filter((x) => x.kind === "unit").length;
  const groups = chosen.length - units;
  const parts = [
    units ? t("glossary:unitCount", { count: units }) : "",
    groups
      ? t("signage:signageCampaignDialog.groupCount", { count: groups })
      : "",
  ].filter(Boolean);
  return parts.length
    ? parts.join(LIST_SEPARATOR)
    : t("signage:signageCampaignDialog.targetSummaryNone");
}

function Section({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <Box>
      <Typography
        sx={{
          typography: "title",
          fontWeight: "fontWeightSemibold",
          color: tokens.ink,
        }}
      >
        {title}
      </Typography>
      {hint && (
        <Typography variant="meta" sx={{ color: tokens.inkMuted, mb: 2 }}>
          {hint}
        </Typography>
      )}
      {!hint && <Box sx={{ mb: 2 }} />}
      {children}
    </Box>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      variant="meta"
      sx={{ fontWeight: "fontWeightSemibold", color: tokens.ink }}
    >
      {children}
    </Typography>
  );
}

function FormatCard({
  format,
  count,
  selected,
  onClick,
}: {
  format: ScreenOrientation;
  count: number;
  selected: boolean;
  onClick: () => void;
}) {
  const { t } = useTranslation(["common", "glossary"]);
  const label =
    format === "portrait"
      ? t("common:labels.portrait")
      : t("common:labels.landscape");
  const Icon = format === "portrait" ? CropPortraitIcon : CropLandscapeIcon;
  return (
    <Box
      role="button"
      aria-pressed={selected}
      tabIndex={0}
      onClick={onClick}
      sx={{
        flex: 1,
        display: "flex",
        alignItems: "center",
        gap: 2,
        p: 2,
        borderRadius: "8px",
        cursor: "pointer",
        border: `1px solid ${selected ? tokens.brand : tokens.divider}`,
        bgcolor: selected ? tokens.brandTintSoft : "transparent",
      }}
    >
      <Box
        sx={{
          p: 1.25,
          borderRadius: "8px",
          bgcolor: tokens.brandTintSoft,
          color: tokens.brand,
          display: "grid",
          placeItems: "center",
        }}
      >
        <Icon />
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: "fontWeightSemibold" }}>
          {label}
        </Typography>
        <Typography variant="meta" sx={{ color: tokens.inkMuted }}>
          {t("glossary:unitCount", { count })}
        </Typography>
      </Box>
      <Checkbox
        size="small"
        checked={selected}
        onChange={onClick}
        inputProps={{ "aria-label": label }}
      />
    </Box>
  );
}

function PreviewMedia({
  items,
  library,
  seconds,
}: {
  items: SignageItem[];
  library: Map<string, AssetSummary>;
  seconds: number;
}) {
  const { t } = useTranslation("signage");
  const first = items[0];
  const thumb = first ? thumbnailFor(library.get(first.assetUrl)) : null;

  return (
    <Box
      sx={{
        position: "relative",
        aspectRatio: "426 / 208",
        borderRadius: "8px",
        overflow: "hidden",
        bgcolor: tokens.rowAlt,
        display: "grid",
        placeItems: "center",
      }}
    >
      {thumb ? (
        <Box
          component="img"
          src={thumb}
          alt=""
          sx={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      ) : (
        <Typography variant="meta" sx={{ color: tokens.inkMuted }}>
          {t("signageCampaignDialog.noMediaChosen")}
        </Typography>
      )}
      {first && (
        <Typography
          variant="micro"
          sx={{
            position: "absolute",
            right: 8,
            bottom: 8,
            px: 0.75,
            py: 0.25,
            borderRadius: "4px",
            bgcolor: "rgba(0,0,0,0.6)",
            color: "#fff",
            fontWeight: "fontWeightSemibold",
          }}
        >
          {durationLabel(seconds)}
        </Typography>
      )}
    </Box>
  );
}

function SummaryRow({
  label,
  value,
  accent,
  last,
}: {
  label: string;
  value: string;
  accent?: boolean;
  last?: boolean;
}) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{
        py: 1.5,
        gap: 2,
        borderBottom: last ? "none" : `1px solid ${tokens.divider}`,
      }}
    >
      <Typography
        variant="meta"
        sx={{ fontWeight: "fontWeightSemibold", color: tokens.inkMuted }}
      >
        {label}
      </Typography>
      <Typography
        variant="meta"
        sx={{
          textAlign: "right",
          color: accent ? tokens.brand : tokens.inkMuted,
          fontWeight: accent ? "fontWeightSemibold" : "fontWeightRegular",
        }}
      >
        {value}
      </Typography>
    </Stack>
  );
}
